import React, { useState } from 'react';
import { Button, Col, InputNumber, Row, Select, Space, Tag, Tooltip, Typography } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    ModelTestResult,
    testModelConnectivity,
} from '../../../services/ai-admin-api';

const { Text } = Typography;

export type ProviderId = 'anthropic' | 'openai' | 'google';

export interface ProviderModelTempValue {
    provider: ProviderId;
    model: string;
    temperature: number;
}

interface Props {
    label?: string;
    value: ProviderModelTempValue;
    onChange: (v: ProviderModelTempValue) => void;
    /** Available models list, typically the JSON parsed from AVAILABLE_*_MODELS system_setting. */
    anthropicModels?: Array<{ id: string; label?: string }>;
    openaiModels?: Array<{ id: string; label?: string }>;
    googleModels?: Array<{ id: string; label?: string }>;
    disabled?: boolean;
    /** When true, the model dropdown gets a "(use platform-wide default)" empty option. Use for per-expert overrides. */
    allowNoModel?: boolean;
}

const PROVIDERS = [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'google', label: 'Google' },
];

function modelsForProvider(
    provider: ProviderId,
    anthropicModels: Array<{ id: string; label?: string }>,
    openaiModels: Array<{ id: string; label?: string }>,
    googleModels: Array<{ id: string; label?: string }>,
): Array<{ id: string; label?: string }> {
    if (provider === 'openai') return openaiModels;
    if (provider === 'google') return googleModels;
    return anthropicModels;
}

/**
 * Standard provider/model/temperature form widget. Shared across every
 * subsystem's Configure tab so the look-and-feel is consistent — Phase 3
 * cutovers should compose this rather than rolling their own.
 */
export const ProviderModelTempForm: React.FC<Props> = ({
    label,
    value,
    onChange,
    anthropicModels = [],
    openaiModels = [],
    googleModels = [],
    disabled,
    allowNoModel,
}) => {
    // Filter models by the currently-selected provider. Admins choose the
    // provider explicitly; the model dropdown reflects that choice.
    const providerModels = modelsForProvider(
        value.provider,
        anthropicModels,
        openaiModels,
        googleModels,
    );
    const providerKnownIds = new Set(providerModels.map((m) => m.id));
    const isOrphan = !!value.model && !providerKnownIds.has(value.model);

    const modelOptions: Array<{ value: string; label: string }> = [];
    if (allowNoModel) {
        modelOptions.push({ value: '', label: '(use platform-wide default)' });
    }
    // If the saved value doesn't belong to the current provider's list, surface
    // it at the top labelled clearly so the admin sees the mismatch and can
    // either change provider or pick a different model.
    if (isOrphan) {
        modelOptions.push({
            value: value.model,
            label: `${value.model} (not in ${value.provider} list — mismatch?)`,
        });
    }
    for (const m of providerModels) {
        modelOptions.push({ value: m.id, label: m.label ?? m.id });
    }

    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<ModelTestResult | null>(null);

    const runTest = async () => {
        if (!jwt || !value.model) return;
        setTesting(true);
        try {
            const result = await testModelConnectivity(jwt, {
                kind: 'chat',
                provider: value.provider,
                modelId: value.model,
            });
            setTestResult(result);
        } catch (err: any) {
            setTestResult({
                ok: false,
                kind: 'chat',
                provider: value.provider,
                modelId: value.model,
                error: err.message || 'Test failed',
            });
        } finally {
            setTesting(false);
        }
    };

    // Reset cached test result when the saved provider/model changes — stale
    // results are misleading.
    React.useEffect(() => {
        setTestResult(null);
    }, [value.provider, value.model]);

    const handleModelChange = (modelId: string) => {
        onChange({ ...value, model: modelId });
    };

    return (
        <div style={{ marginBottom: 8 }}>
            {label && (
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    {label}
                </Text>
            )}
            <Row gutter={8}>
                <Col xs={24} sm={6}>
                    <Select
                        style={{ width: '100%' }}
                        value={value.provider}
                        onChange={(provider) => {
                            // Clear a now-orphaned model when switching providers — the
                            // current model id likely doesn't belong to the new provider.
                            const nextModels = modelsForProvider(
                                provider as ProviderId,
                                anthropicModels,
                                openaiModels,
                                googleModels,
                            );
                            const stillValid = nextModels.some((m) => m.id === value.model);
                            onChange({
                                ...value,
                                provider: provider as ProviderId,
                                model: stillValid ? value.model : '',
                            });
                        }}
                        options={PROVIDERS}
                        disabled={disabled}
                    />
                </Col>
                <Col xs={24} sm={12}>
                    <Select
                        style={{ width: '100%' }}
                        value={value.model ?? ''}
                        onChange={handleModelChange}
                        options={modelOptions}
                        disabled={disabled}
                        placeholder="Select model"
                        showSearch
                        optionFilterProp="label"
                    />
                </Col>
                <Col xs={24} sm={6}>
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={2}
                        step={0.05}
                        value={value.temperature}
                        onChange={(t) =>
                            onChange({ ...value, temperature: Number(t ?? 0) })
                        }
                        disabled={disabled}
                        addonBefore="temp"
                    />
                </Col>
            </Row>
            <Space style={{ marginTop: 6 }} size={4} wrap>
                <Tooltip title="Send a tiny ping to verify this provider+model combination works.">
                    <Button
                        size="small"
                        icon={<ThunderboltOutlined />}
                        loading={testing}
                        onClick={runTest}
                        disabled={disabled || !value.model}
                    >
                        Test
                    </Button>
                </Tooltip>
                {testResult?.ok && (
                    <Tooltip title={testResult.detail}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                            OK ({testResult.latencyMs ?? '?'}ms)
                        </Tag>
                    </Tooltip>
                )}
                {testResult && !testResult.ok && (
                    <Tooltip title={testResult.error}>
                        <Tag color="red" icon={<CloseCircleOutlined />}>
                            Failed — {testResult.error?.slice(0, 80) ?? 'see tooltip'}
                        </Tag>
                    </Tooltip>
                )}
            </Space>
        </div>
    );
};
